'use client';

import * as React from 'react';
import { cn } from '../utils/cn';

type InfoSectionProps = {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'primary' | 'nested';
  title?: string;
  titleIcon?: React.ReactNode;
  badge?: React.ReactNode;
};

/**
 * InfoSection - Reusable glass container component
 *
 * A beautiful liquid glass container for displaying grouped information.
 * Features subtle transparency, inner light effect, and proper shadows.
 *
 * @example
 * // Basic usage
 * <InfoSection title="Organization Details" titleIcon={<Building2 />}>
 * <p>Content here</p>
 * </InfoSection>
 *
 * @example
 * // With variant and badge
 * <InfoSection
 * title="Active Settings"
 * titleIcon={<Sparkles />}
 * variant="primary"
 * badge={<span>Read-only</span>}
 * >
 * <p>Content here</p>
 * </InfoSection>
 *
 * @example
 * // Nested section
 * <InfoSection variant="nested" title="AI Behavior">
 * <p>Detailed instructions...</p>
 * </InfoSection>
 */
export const InfoSection = React.forwardRef<HTMLDivElement, InfoSectionProps>(
  ({ children, className, variant = 'default', title, titleIcon, badge }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'glass-info-section flex flex-col gap-4',
          variant === 'primary' && 'bg-primary/5',
          variant === 'nested' && 'mt-2',
          className
        )}
      >
        {(title || titleIcon || badge) && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {titleIcon && (
                <div
                  className={cn(
                    'flex-shrink-0',
                    variant === 'primary' && 'text-primary',
                    variant === 'nested' && 'text-primary'
                  )}
                >
                  {titleIcon}
                </div>
              )}
              {title && (
                <h3 className={cn('font-semibold', variant === 'nested' ? 'text-sm' : 'text-lg')}>
                  {title}
                </h3>
              )}
            </div>
            {badge && (
              <div className="text-xs px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                {badge}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
    );
  }
);
InfoSection.displayName = 'InfoSection';

type InfoRowProps = {
  label: string;
  value: string | React.ReactNode;
  className?: string;
};

/**
 * InfoRow - Label/Value pair component
 *
 * Displays a label and value in a consistent format.
 * Use inside InfoSection for structured data display.
 *
 * @example
 * <InfoRow label="Organization" value="Oxford medical org" />
 */
export const InfoRow = React.forwardRef<HTMLDivElement, InfoRowProps>(
  ({ label, value, className }, ref) => {
    return (
      <div ref={ref} className={cn('flex flex-col sm:flex-row gap-1 sm:gap-2', className)}>
        <span className="text-muted-foreground font-medium whitespace-nowrap">{label}:</span>
        <span className="font-semibold text-foreground break-words">{value}</span>
      </div>
    );
  }
);
InfoRow.displayName = 'InfoRow';

type InfoTextBlockProps = {
  label: string;
  children: React.ReactNode;
  className?: string;
};

/**
 * InfoTextBlock - Label with multi-line text content
 *
 * For displaying labeled text blocks with a colored left border.
 * Use for longer content like instructions or descriptions.
 *
 * @example
 * <InfoTextBlock label="Other Details">
 * {settings.otherDetails}
 * </InfoTextBlock>
 */
export const InfoTextBlock = React.forwardRef<HTMLDivElement, InfoTextBlockProps>(
  ({ label, children, className }, ref) => {
    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)}>
        <span className="text-sm font-medium text-muted-foreground">{label}:</span>
        <div className="text-secondary-enhanced pl-3 border-l-2 border-primary/30">{children}</div>
      </div>
    );
  }
);
InfoTextBlock.displayName = 'InfoTextBlock';
